import { useState, useEffect, useCallback, useRef } from 'react';
import { Settings, Copy, Check, Palette, Globe, Code, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

interface MarinaConfig {
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  contact_vhf: string | null;
  address: string | null;
  active_languages: string[];
  season_open: string | null;
  season_close: string | null;
  domain_whitelist: string[];
  privacy_policy_url: string | null;
  security?: { domain_whitelist_missing?: boolean };
}

const ALL_LANGUAGES = [
  { code: 'et', label: 'Estonian' },
  { code: 'en', label: 'English' },
  { code: 'fi', label: 'Finnish' },
  { code: 'de', label: 'German' },
];

export function WidgetSettingsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId, isOrgAdmin, profile } = useAuth();

  const needsMarinaSelection =
    isOrgAdmin &&
    (profile?.accessibleMarinaIds?.length ?? 0) > 1 &&
    activeMarinaId === null;
  const [config, setConfig] = useState<MarinaConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const [color, setColor] = useState('#2563eb');
  const [logoUrl, setLogoUrl] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [vhf, setVhf] = useState('');
  const [address, setAddress] = useState('');
  const [seasonOpen, setSeasonOpen] = useState('');
  const [seasonClose, setSeasonClose] = useState('');
  const [domains, setDomains] = useState('');
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
  const [domainWhitelistMissing, setDomainWhitelistMissing] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchConfig = useCallback(async () => {
    if (needsMarinaSelection) {
      setConfig(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.getMarina() as unknown as MarinaConfig;
      setConfig(data);
      setDomainWhitelistMissing(Boolean(data.security?.domain_whitelist_missing));
      setColor(data.primary_color ?? '#2563eb');
      setLogoUrl(data.logo_url ?? '');
      setLanguages(data.active_languages ?? []);
      setPhone(data.contact_phone ?? '');
      setEmail(data.contact_email ?? '');
      setVhf(data.contact_vhf ?? '');
      setAddress(data.address ?? '');
      setSeasonOpen(data.season_open ?? '');
      setSeasonClose(data.season_close ?? '');
      setDomains((data.domain_whitelist ?? []).join('\n'));
      setPrivacyPolicyUrl(data.privacy_policy_url ?? '');
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId, needsMarinaSelection]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const MM_DD = /^\d{2}-\d{2}$/;

  const handleSave = async () => {
    if (saving) return;
    if (needsMarinaSelection) return;
    if (languages.length === 0) {
      toast.error(t('widgetSettings.atLeastOneLang'));
      return;
    }
    if (seasonOpen && !MM_DD.test(seasonOpen)) {
      toast.error(t('widgetSettings.seasonOpenFormat'));
      return;
    }
    if (seasonClose && !MM_DD.test(seasonClose)) {
      toast.error(t('widgetSettings.seasonCloseFormat'));
      return;
    }
    setSaving(true);
    try {
      const wl = domains.split('\n').map((d) => d.trim()).filter(Boolean);
      await api.updateMarina({
        primary_color: color || null,
        logo_url: logoUrl || null,
        active_languages: languages,
        contact_phone: phone || null,
        contact_email: email || null,
        contact_vhf: vhf || null,
        address: address || null,
        season_open: seasonOpen || null,
        season_close: seasonClose || null,
        domain_whitelist: wl,
        privacy_policy_url: privacyPolicyUrl.trim() || null,
      });
      toast.success(t('settings.settingsSaved'));
      await fetchConfig();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const toggleLanguage = (code: string) => {
    setLanguages((prev) =>
      prev.includes(code) ? prev.filter((l) => l !== code) : [...prev, code],
    );
  };

  const widgetBase = import.meta.env.VITE_WIDGET_URL ?? 'https://harbormaster.app';
  const apiBase = import.meta.env.VITE_API_URL ?? '';
  const embedCode = config
    ? (() => {
        const params = new URLSearchParams({ marina: config.slug });
        if (apiBase) params.set('api', apiBase);
        return `<script src="${widgetBase}/widget.js?${params.toString()}" async></script>`;
      })()
    : '';

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      toast.success(t('settings.embedCodeCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('settings.copyFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{t('widgetSettings.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('widgetSettings.subtitle')}</p>
      </div>

      {needsMarinaSelection && (
        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-950 text-sm">
          {t('settings.selectMarinaHint')}
        </div>
      )}

      {domainWhitelistMissing && !needsMarinaSelection && (
        <div className="flex gap-2 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-950 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
          <span>{t('settings.domainWhitelistSecurityWarning')}</span>
        </div>
      )}

      {/* Branding */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Palette className="w-4 h-4 text-brand-600" /> {t('settings.branding')}
        </h2>
        <div>
          <label className={labelClass}>{t('settings.brandColor')}</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              maxLength={7}
              className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/40"
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('settings.logoUrl')}</label>
          <input type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://..." className={inputClass} />
          <p className="text-xs text-gray-400 mt-1">{t('widgetSettings.logoUrlHint')}</p>
        </div>
      </div>

      {/* Languages */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Globe className="w-4 h-4 text-brand-600" /> {t('settings.languages')}
        </h2>
        <div className="flex flex-wrap gap-2">
          {ALL_LANGUAGES.map((l) => {
            const isActive = languages.includes(l.code);
            const isLastActive = isActive && languages.length === 1;
            return (
              <button
                key={l.code}
                onClick={() => !isLastActive && toggleLanguage(l.code)}
                disabled={isLastActive}
                title={isLastActive ? t('widgetSettings.atLeastOneLang') : undefined}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  isActive
                    ? 'bg-brand-50 border-brand-300 text-brand-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                } ${isLastActive ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {l.label}
              </button>
            );
          })}
        </div>
        {languages.length === 0 && (
          <p className="text-xs text-red-600 mt-1">{t('widgetSettings.atLeastOneLang')}.</p>
        )}
      </div>

      {/* Contact info */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-4 h-4 text-brand-600" /> {t('widgetSettings.contactConfig')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('settings.phone')}</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+372..." className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('settings.email')}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('settings.vhfChannel')}</label>
            <input type="text" value={vhf} onChange={(e) => setVhf(e.target.value)} placeholder="Ch 16" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('settings.address')}</label>
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className={inputClass} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('settings.seasonOpen')}</label>
            <input type="text" value={seasonOpen} onChange={(e) => setSeasonOpen(e.target.value)} placeholder="05-01" maxLength={5} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('settings.seasonClose')}</label>
            <input type="text" value={seasonClose} onChange={(e) => setSeasonClose(e.target.value)} placeholder="10-01" maxLength={5} className={inputClass} />
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('settings.domainWhitelist')}</label>
          <textarea
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            rows={3}
            placeholder="yourmarina.com&#10;www.yourmarina.com"
            className={inputClass + ' font-mono'}
          />
          <p className="text-xs text-gray-400 mt-1">{t('settings.domainWhitelistHint')}</p>
        </div>
        <div>
          <label className={labelClass}>{t('widgetSettings.privacyPolicyUrl')}</label>
          <input
            type="url"
            value={privacyPolicyUrl}
            onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
            placeholder="https://..."
            maxLength={500}
            className={inputClass}
          />
          <p className="text-xs text-gray-400 mt-1">{t('widgetSettings.privacyPolicyUrlHint')}</p>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving || needsMarinaSelection}
        className="px-6 py-2.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
      >
        {saving ? t('common.saving') : t('settings.saveSettings')}
      </button>

      {/* Embed code */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Code className="w-4 h-4 text-brand-600" /> {t('settings.embedCode')}
        </h2>
        <p className="text-xs text-gray-500">{t('widgetSettings.embedCodeHint')}</p>
        <div className="relative">
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-700 overflow-x-auto">
            {embedCode}
          </pre>
          <button
            onClick={copyEmbed}
            className="absolute top-2 right-2 p-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 text-gray-500"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
